import { z } from 'zod';
import { TextKey } from '@mews-commander/enums';
import { CustomErrorParams, CustomErrorType } from '@mews/framework';
import { RoutingRulesFormStepName } from '../types';

export enum ServiceFormFields {
    Enabled = 'serviceEnabled',
    Options = 'serviceOptions',
    Products = 'serviceProducts',
    Rates = 'serviceRates',
}

export enum AdditionalServiceFormFields {
    Enabled = 'serviceEnabled',
    Options = 'serviceOptions',
    Products = 'serviceProducts',
    CustomItems = 'customItems',
}

export enum ItemsAndConditionsFormFields {
    Services = 'services',
    AdditionalServices = 'additionalServices',
    RatesEnabled = 'ratesEnabled',
    CompaniesEnabled = 'companiesEnabled',
    Companies = 'companies',
    PrepaidReservationsEnabled = 'prepaidReservationsEnabled',
    GroupsAndReservationsEnabled = 'groupsAndReservationsEnabled',
    GroupsAndReservations = 'groupsAndReservations',
    GuestProfilesEnabled = 'guestProfilesEnabled',
    GuestProfiles = 'guestProfiles',
}

export enum RatesFormFields {
    Type = 'type',
    SelectedRates = 'selectedRates',
}

export enum RatesOptionsNames {
    All = 'all',
    Selected = 'selected',
}

export enum ProductsFormFields {
    Type = 'type',
    SelectedProducts = 'selectedProducts',
    SelectedProductCategories = 'selectedProductCategories',
    SelectedAccountingCategories = 'selectedAccountingCategories',
}

export enum ProductsOptionsNames {
    All = 'all',
    SelectedProducts = 'selectedProducts',
    SelectedProductCategories = 'selectedProductCategories',
    SelectedAccountingCategories = 'selectedAccountingCategories',
}

export enum CustomItemsFormFields {
    Type = 'type',
    SelectedAccountingCategories = 'selectedAccountingCategories',
}
export enum CustomItemsOptionsNames {
    All = 'all',
    SelectedAccountingCategories = 'selectedAccountingCategories',
}

export enum OptionsNames {
    TimeUnit = 'timeUnit',
    CityTaxes = 'cityTaxes',
    Deposits = 'deposits',
    AdditionalExpenses = 'additionalExpenses',
    Products = 'products',
}

export enum AdditionalServiceOptionsNames {
    Products = 'products',
    CustomItems = 'customItems',
}

export enum CompaniesFormFields {
    LinkAsCompany = 'linkAsCompany',
    LinkAsTravelAgency = 'linkAsTravelAgency',
    Name = 'name',
}

export const MAX_SERVICES_COUNT = 10;
export const MAX_SERVICE_ITEMS_COUNT = 100;

export const ratesSchema = z
    .object({
        [RatesFormFields.Type]: z.nativeEnum(RatesOptionsNames),
        [RatesFormFields.SelectedRates]: z.array(z.string()).optional(),
    })
    .optional()
    .superRefine((data, ctx) => {
        if (data == null || data[RatesFormFields.Type] === RatesOptionsNames.All) return;
        const rates = data[RatesFormFields.SelectedRates] ?? [];
        if (rates.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [RatesFormFields.Type],
                params: {
                    errorType: CustomErrorType.Other,
                    textKey: TextKey.RoutingRulesFormRatesDialogValidationMessage,
                } satisfies CustomErrorParams,
            });
        } else if (rates.length > MAX_SERVICE_ITEMS_COUNT) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [RatesFormFields.Type],
                params: {
                    errorType: CustomErrorType.Other,
                    textKey: TextKey.RoutingRulesFormRatesMaxCountValidationMessage,
                } satisfies CustomErrorParams,
            });
        }
    });

export const productsSchema = z
    .object({
        [ProductsFormFields.Type]: z.nativeEnum(ProductsOptionsNames),
        [ProductsFormFields.SelectedProducts]: z.array(z.string()).optional(),
        [ProductsFormFields.SelectedProductCategories]: z.array(z.string()).optional(),
        [ProductsFormFields.SelectedAccountingCategories]: z.array(z.string()).optional(),
    })
    .optional()
    .superRefine((data, ctx) => {
        if (data == null) return;

        const validations = {
            [ProductsOptionsNames.SelectedProducts]: {
                field: ProductsFormFields.SelectedProducts,
                emptyTextKey: TextKey.RoutingRulesFormProductsDialogValidationMessage,
                maxTextKey: TextKey.RoutingRulesFormProductsMaxCountValidationMessage,
            },
            [ProductsOptionsNames.SelectedProductCategories]: {
                field: ProductsFormFields.SelectedProductCategories,
                emptyTextKey: TextKey.RoutingRulesFormProductCategoriesDialogValidationMessage,
                maxTextKey: TextKey.RoutingRulesFormProductCategoriesMaxCountValidationMessage,
            },
            [ProductsOptionsNames.SelectedAccountingCategories]: {
                field: ProductsFormFields.SelectedAccountingCategories,
                emptyTextKey: TextKey.RoutingRulesFormAccountingCategoriesDialogValidationMessage,
                maxTextKey: TextKey.RoutingRulesFormAccountingCategoriesMaxCountValidationMessage,
            },
        };

        const option = data[ProductsFormFields.Type] as Exclude<ProductsOptionsNames, ProductsOptionsNames.All>;
        const validation = validations[option];
        if (!validation) return;

        const items = data[validation.field] ?? [];
        if (items.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [ProductsFormFields.Type],
                params: {
                    errorType: CustomErrorType.Other,
                    textKey: validation.emptyTextKey,
                } satisfies CustomErrorParams,
            });
        } else if (items.length > MAX_SERVICE_ITEMS_COUNT) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [ProductsFormFields.Type],
                params: {
                    errorType: CustomErrorType.Other,
                    textKey: validation.maxTextKey,
                } satisfies CustomErrorParams,
            });
        }
    });

export type ProductsFormValues = z.infer<typeof productsSchema>;

export const customItemsSchema = z
    .object({
        [CustomItemsFormFields.Type]: z.nativeEnum(CustomItemsOptionsNames),
        [CustomItemsFormFields.SelectedAccountingCategories]: z.array(z.string()).optional(),
    })
    .optional()
    .superRefine((data, ctx) => {
        if (data == null || data[CustomItemsFormFields.Type] === CustomItemsOptionsNames.All) return;
        const accountingCategories = data[CustomItemsFormFields.SelectedAccountingCategories] ?? [];
        if (accountingCategories.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [CustomItemsFormFields.Type],
                params: {
                    errorType: CustomErrorType.Other,
                    textKey: TextKey.RoutingRulesFormAccountingCategoriesDialogValidationMessage,
                } satisfies CustomErrorParams,
            });
        } else if (accountingCategories.length > MAX_SERVICE_ITEMS_COUNT) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [CustomItemsFormFields.Type],
                params: {
                    errorType: CustomErrorType.Other,
                    textKey: TextKey.RoutingRulesFormAccountingCategoriesMaxCountValidationMessage,
                } satisfies CustomErrorParams,
            });
        }
    });

export type CustomItemsFormValues = z.infer<typeof customItemsSchema>;

export const serviceSchema = z.union([
    z.object({
        [ServiceFormFields.Enabled]: z.preprocess((val) => (val === undefined ? false : val), z.literal(false)),
        [ServiceFormFields.Options]: z.any().optional(),
        [ServiceFormFields.Rates]: z.any().optional(),
        [ServiceFormFields.Products]: z.any().optional(),
    }),
    z
        .object({
            [ServiceFormFields.Enabled]: z.literal(true),
            [ServiceFormFields.Options]: z.array(z.nativeEnum(OptionsNames)).default([]),
            [ServiceFormFields.Rates]: ratesSchema,
            [ServiceFormFields.Products]: productsSchema,
        })
        .refine((data) => (data.serviceEnabled && data.serviceOptions.length > 0) || !data.serviceEnabled, {
            message: TextKey.RoutingRulesFormItemsOptionsErrorMessage,
            path: [ServiceFormFields.Options],
        }),
]);

const servicesSchema = z
    .record(serviceSchema)
    .refine((services) => Object.values(services).some((service) => service[ServiceFormFields.Enabled]), {
        params: {
            errorType: CustomErrorType.Other,
            textKey: TextKey.RoutingRulesFormEnabledServicesError,
        } satisfies CustomErrorParams,
    })
    .refine(
        (services) =>
            Object.values(services).filter((service) => service[ServiceFormFields.Enabled]).length <=
            MAX_SERVICES_COUNT,
        {
            params: {
                errorType: CustomErrorType.Other,
                textKey: TextKey.RoutingRulesFormMaxServicesError,
            } satisfies CustomErrorParams,
        }
    );

export type ServiceFormValues = z.infer<typeof serviceSchema>;
export type ServicesFormValues = z.infer<typeof servicesSchema>;

const additionalServiceSchema = z
    .union([
        z.object({
            [AdditionalServiceFormFields.Enabled]: z.preprocess(
                (val) => (val === undefined ? false : val),
                z.literal(false)
            ),
            [AdditionalServiceFormFields.Options]: z.any().optional(),
            [AdditionalServiceFormFields.Products]: z.any().optional(),
            [AdditionalServiceFormFields.CustomItems]: z.any().optional(),
        }),
        z.object({
            [AdditionalServiceFormFields.Enabled]: z.boolean().default(false),
            [AdditionalServiceFormFields.Options]: z.array(z.nativeEnum(AdditionalServiceOptionsNames)).default([]),
            [AdditionalServiceFormFields.Products]: productsSchema,
            [AdditionalServiceFormFields.CustomItems]: customItemsSchema,
        }),
    ])
    .refine((data) => (data.serviceEnabled && data.serviceOptions.length > 0) || !data.serviceEnabled, {
        message: TextKey.RoutingRulesFormItemsOptionsErrorMessage,
        path: [ServiceFormFields.Options],
    });
const additionalServicesSchema = z
    .record(additionalServiceSchema)
    .refine(
        (additionalServices) =>
            Object.values(additionalServices).filter(
                (additionalService) => additionalService[AdditionalServiceFormFields.Enabled]
            ).length <= MAX_SERVICES_COUNT,
        {
            params: {
                errorType: CustomErrorType.Other,
                textKey: TextKey.RoutingRulesFormMaxAdditionalServicesError,
            } satisfies CustomErrorParams,
        }
    );
export type AdditionalServiceFormValues = z.infer<typeof additionalServiceSchema>;
export type AdditionalServicesFormValues = z.infer<typeof additionalServicesSchema>;

export const companiesSchema = z
    .record(
        z.object({
            [CompaniesFormFields.LinkAsCompany]: z.boolean().optional(),
            [CompaniesFormFields.LinkAsTravelAgency]: z.boolean().optional(),
            [CompaniesFormFields.Name]: z.string(),
        })
    )
    .refine(
        (companies) =>
            Object.entries(companies).filter(([_, company]) => company.linkAsCompany || company.linkAsTravelAgency)
                .length > 0
    );

export type CompaniesFormValues = z.infer<typeof companiesSchema>;

export const itemsAndConditionsSchema = z.object({
    [RoutingRulesFormStepName.ItemsAndConditions]: z
        .object({
            [ItemsAndConditionsFormFields.Services]: servicesSchema,
            [ItemsAndConditionsFormFields.AdditionalServices]: additionalServicesSchema.optional(),
            [ItemsAndConditionsFormFields.RatesEnabled]: z.boolean().default(false),
            [ItemsAndConditionsFormFields.CompaniesEnabled]: z.boolean().default(false),
            [ItemsAndConditionsFormFields.Companies]: companiesSchema.or(z.object({})),
            [ItemsAndConditionsFormFields.PrepaidReservationsEnabled]: z.boolean().default(false),
            [ItemsAndConditionsFormFields.GroupsAndReservationsEnabled]: z.boolean().default(false),
            [ItemsAndConditionsFormFields.GroupsAndReservations]: z
                .object({
                    reservationIds: z.array(z.string()).default([]),
                    reservationGroupIds: z.array(z.string()).default([]),
                })
                .optional(),
            [ItemsAndConditionsFormFields.GuestProfilesEnabled]: z.boolean().default(false),
            [ItemsAndConditionsFormFields.GuestProfiles]: z.array(z.string()).default([]),
        })
        .superRefine((data, ctx) => {
            const enabled = data[ItemsAndConditionsFormFields.GuestProfilesEnabled];
            const profiles = data[ItemsAndConditionsFormFields.GuestProfiles];

            if (enabled && profiles.length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [ItemsAndConditionsFormFields.GuestProfiles],
                    params: {
                        errorType: CustomErrorType.Other,
                        textKey: TextKey.ValidateRequired,
                    } satisfies CustomErrorParams,
                });
            }
        }),
});

export type ItemsAndConditionsSchemaFormValues = z.infer<
    typeof itemsAndConditionsSchema
>[RoutingRulesFormStepName.ItemsAndConditions];
